import { LitElement, html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import { styles } from "./stream-recorder.css";

type RecorderState = "idle" | "starting" | "streaming" | "error";

/**
 * Stream Recorder component - WebRTC WHIP streaming from webcam/microphone.
 * Uses WHIP protocol to publish media to a streaming server.
 * Accepts native camera output without aspect ratio transformation.
 */
@customElement("stream-recorder")
export class StreamRecorder extends LitElement {
  static override styles = styles;

  @property() streamId?: string;

  @property({ type: Boolean, reflect: true, attribute: "show-preview" }) showPreview = true;

  @property({ type: Boolean, attribute: "enable-audio" }) enableAudio = true;

  @property({ type: Boolean, attribute: "hide-controls" }) hideControls = false;

  maxVideoBitrate = 2500000; // 2500 kbps (matching server)

  maxAudioBitrate = 128000; // 128 kbps

  maxFramerate = 24;

  @state() private isPaused = false;

  private get WHIP_BASE_URL(): string {
    const isLocalhost = (location.origin ?? "localhost").includes("localhost");
    const isProduction = location.origin.includes("worldofwow.com");
    const liveDomain = isLocalhost
      ? "live.worldofwow.dev"
      : isProduction
        ? "live.worldofwow.com"
        : "live.worldofwow.dev";
    return `https://${liveDomain}:8889/streams/`;
  }

  private get whipEndpoint(): string {
    return `${this.WHIP_BASE_URL}${this.streamId}/whip`;
  }

  /**
   * Raw fetch without Application Insights instrumentation.
   * AI adds request-context headers that cause CORS issues with the streaming server.
   */
  private async rawFetch(
    url: string,
    options: { method: string; headers: Record<string, string>; body?: string },
  ): Promise<{
    ok: boolean;
    status: number;
    text: () => Promise<string>;
    headers: { get: (name: string) => string | null };
  }> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method, url, true);

      for (const [key, value] of Object.entries(options.headers)) {
        xhr.setRequestHeader(key, value);
      }

      xhr.onload = () => {
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          text: () => Promise.resolve(xhr.responseText),
          headers: {
            get: (name: string) => xhr.getResponseHeader(name),
          },
        });
      };

      xhr.onerror = () => reject(new Error("Network error"));
      xhr.send(options.body);
    });
  }

  @query("video")
  private videoElement?: HTMLVideoElement;

  @state() private recorderState: RecorderState = "idle";

  @state() private errorMessage = "";

  private pc: RTCPeerConnection | null = null;
  private mediaStream: MediaStream | null = null;
  private whipResourceURL: string | null = null;

  override disconnectedCallback() {
    super.disconnectedCallback();
    void this.stopStreaming();
  }

  async startStreaming(): Promise<void> {
    if (this.recorderState === "streaming" || this.recorderState === "starting") {
      return;
    }

    if (!this.streamId) {
      this.handleError("Stream ID is required");
      return;
    }

    this.recorderState = "starting";
    this.errorMessage = "";

    try {
      // Get webcam/microphone stream with reasonable defaults
      // Accept native camera output without forcing aspect ratio
      const videoConstraints = {
        facingMode: { ideal: "user" },
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 24, max: this.maxFramerate },
      };

      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: this.enableAudio,
      });

      // Show preview if enabled
      if (this.videoElement && this.showPreview) {
        this.videoElement.srcObject = this.mediaStream;
      }

      // Create peer connection
      this.pc = new RTCPeerConnection({
        iceServers: [{ urls: ["stun:stun.l.google.com:19302"] }],
      });

      // Add tracks to peer connection
      this.mediaStream.getTracks().forEach((track) => {
        if (this.pc && this.mediaStream) {
          this.pc.addTrack(track, this.mediaStream);
        }
      });

      // Set codec preferences for H.264 (HLS compatibility)
      await this.setCodecPreferences();

      // Apply bitrate constraints
      await this.applyBitrateConstraints();

      // Handle ICE candidates
      this.pc.onicecandidate = async (event) => {
        if (!event.candidate || !this.whipResourceURL) return;

        try {
          await this.rawFetch(this.whipResourceURL, {
            method: "PATCH",
            headers: { "Content-Type": "application/trickle-ice-sdpfrag" },
            body: `a=${event.candidate.candidate}`,
          });
        } catch (error) {
          console.warn("ICE trickle failed:", error);
        }
      };

      // Monitor connection state
      this.pc.onconnectionstatechange = () => {
        if (!this.pc) return;

        switch (this.pc.connectionState) {
          case "connected":
            this.recorderState = "streaming";
            this.dispatchEvent(new CustomEvent("streaming-started", { bubbles: true, composed: true }));
            break;
          case "disconnected":
            console.warn("WebRTC connection disconnected, attempting reconnect...");
            break;
          case "failed":
            this.handleError("Connection failed. Please retry.");
            break;
          case "closed":
            if (this.recorderState === "streaming") {
              this.recorderState = "idle";
              this.dispatchEvent(new CustomEvent("streaming-stopped", { bubbles: true, composed: true }));
            }
            break;
        }
      };

      // Create and send offer
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);

      // POST to WHIP endpoint (use rawFetch to bypass Application Insights headers)
      const response = await this.rawFetch(this.whipEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/sdp" },
        body: offer.sdp,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`WHIP POST failed (${response.status}): ${errorText.slice(0, 100)}`);
      }

      // MediaMTX returns a relative Location header (e.g., /streams/{id}/whip/{secret})
      // We must resolve it against the WHIP endpoint base URL, not the page origin
      const locationHeader = response.headers.get("Location");
      if (locationHeader) {
        this.whipResourceURL = new URL(locationHeader, this.WHIP_BASE_URL).href;
      }
      const answerSdp = await response.text();

      await this.pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

      console.log("WHIP stream started");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to start streaming";
      this.handleError(message);
    }
  }

  async stopStreaming(): Promise<void> {
    // Stop all media tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    // Close peer connection
    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }

    // Clear video preview
    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }

    this.whipResourceURL = null;

    if (this.recorderState === "streaming") {
      this.dispatchEvent(new CustomEvent("streaming-stopped", { bubbles: true, composed: true }));
    }

    this.recorderState = "idle";
    this.isPaused = false;
    this.errorMessage = "";
  }

  pauseStreaming(): void {
    if (this.recorderState !== "streaming" || this.isPaused) return;

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.enabled = false;
      });
    }

    this.isPaused = true;
    this.dispatchEvent(new CustomEvent("streaming-paused", { bubbles: true, composed: true }));
  }

  resumeStreaming(): void {
    if (this.recorderState !== "streaming" || !this.isPaused) return;

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.enabled = true;
      });
    }

    this.isPaused = false;
    this.dispatchEvent(new CustomEvent("streaming-resumed", { bubbles: true, composed: true }));
  }

  getIsPaused(): boolean {
    return this.isPaused;
  }

  getRecorderState(): RecorderState {
    return this.recorderState;
  }

  private async setCodecPreferences(): Promise<void> {
    if (!this.pc) return;

    const codecs = RTCRtpSender.getCapabilities("video")?.codecs ?? [];
    console.log(
      "Available video codecs:",
      codecs.map((c) => c.mimeType),
    );

    const transceivers = this.pc.getTransceivers();
    for (const transceiver of transceivers) {
      if (transceiver.sender.track?.kind === "video") {
        // Get H.264 codecs, prefer constrained-baseline for compatibility
        const h264Baseline = codecs.filter(
          (c) => c.mimeType === "video/H264" && c.sdpFmtpLine?.includes("profile-level-id=42"),
        );
        const h264Other = codecs.filter(
          (c) => c.mimeType === "video/H264" && !c.sdpFmtpLine?.includes("profile-level-id=42"),
        );
        const vp8Codecs = codecs.filter((c) => c.mimeType === "video/VP8");

        // Prefer H.264 baseline, then other H.264, then VP8 as fallback
        const preferredCodecs = [...h264Baseline, ...h264Other, ...vp8Codecs];

        if (preferredCodecs.length > 0) {
          try {
            transceiver.setCodecPreferences(preferredCodecs);
            console.log(
              "Codec preferences set:",
              preferredCodecs.map((c) => `${c.mimeType} ${c.sdpFmtpLine ?? ""}`),
            );
          } catch (error) {
            console.warn("setCodecPreferences failed:", error);
          }
        }
      }
    }
  }

  private async applyBitrateConstraints(): Promise<void> {
    if (!this.pc) return;

    const senders = this.pc.getSenders();
    for (const sender of senders) {
      if (sender.track?.kind === "video") {
        const params = sender.getParameters();
        if (!params.encodings || params.encodings.length === 0) {
          params.encodings = [{}];
        }
        const encoding = params.encodings[0]!;
        encoding.maxBitrate = this.maxVideoBitrate;
        encoding.maxFramerate = this.maxFramerate;
        await sender.setParameters(params);
        console.log(`Video bitrate constrained to ${this.maxVideoBitrate / 1000} kbps, ${this.maxFramerate}fps`);
      }
      if (sender.track?.kind === "audio") {
        const params = sender.getParameters();
        if (!params.encodings || params.encodings.length === 0) {
          params.encodings = [{}];
        }
        const encoding = params.encodings[0]!;
        encoding.maxBitrate = this.maxAudioBitrate;
        await sender.setParameters(params);
        console.log(`Audio bitrate constrained to ${this.maxAudioBitrate / 1000} kbps`);
      }
    }
  }

  private handleError(message: string): void {
    this.recorderState = "error";
    this.errorMessage = message;
    this.dispatchEvent(
      new CustomEvent("streaming-error", {
        detail: { message },
        bubbles: true,
        composed: true,
      }),
    );
    void this.stopStreaming();
  }

  private handleStartClick = (): void => {
    void this.startStreaming();
  };

  private handleStopClick = (): void => {
    void this.stopStreaming();
  };

  private getStatusText(): string {
    switch (this.recorderState) {
      case "idle":
        return "Ready";
      case "starting":
        return "Starting...";
      case "streaming":
        return "Live";
      case "error":
        return "Error";
    }
  }

  override render() {
    const isStreaming = this.recorderState === "streaming";
    const isStarting = this.recorderState === "starting";
    const isError = this.recorderState === "error";

    const previewClasses = {
      "preview-wrapper": true,
      hidden: !this.showPreview,
      paused: this.isPaused,
    };

    const statusDotClasses = {
      "status-dot": true,
      streaming: isStreaming && !this.isPaused,
      starting: isStarting,
      error: isError,
      paused: this.isPaused,
    };

    return html`
      <div class="recorder-container">
        <div class=${classMap(previewClasses)}>
          <video autoplay muted playsinline></video>
          ${this.isPaused ? html`<div class="paused-overlay">PAUSED</div>` : null}
        </div>

        ${!this.hideControls
          ? html`
              <div class="controls">
                ${isStreaming
                  ? html` <button class="stream-button stop" @click=${this.handleStopClick}>Stop Streaming</button> `
                  : html`
                      <button class="stream-button start" ?disabled=${isStarting} @click=${this.handleStartClick}>
                        ${isStarting ? "Starting..." : "Start Streaming"}
                      </button>
                    `}

                <div class="status-indicator">
                  <div class=${classMap(statusDotClasses)}></div>
                  <span>${this.getStatusText()}</span>
                </div>
              </div>
            `
          : null}
        ${this.errorMessage ? html` <div class="error-message">${this.errorMessage}</div> ` : null}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "stream-recorder": StreamRecorder;
  }
}
